// 순환 import 방지: 배지 컴포넌트를 끌어오는 index 대신 액션/상수 파일을 직접 참조한다
import { setMediaHealth } from '../../../features/base/media-health/actions';
import { MEDIA_HEALTH } from '../../../features/base/media-health/constants';
import { JitsiConferenceErrors, JitsiConferenceEvents } from '../../../features/base/lib-jitsi-meet';
import { MEDIA_TYPE } from '../../../features/base/media';
import { ROLE, getLocalMember } from '../../../features/base/members';
import { getPropertyValue } from '../../../features/base/settings';
import { getLocalVideoTrack, getTrackState } from '../../../features/base/tracks';

/*
 * senderHealth — 로컬(특히 송출자=강사) 미디어 연결 감시 + 단계적 자가 복구.
 *
 * 배경(2026-09 "강사 화면 검정 수 분 지속" 사고, teacher-blackscreen-audit-2026-09-18.md):
 *  - 강사 → 브리지 송신 경로(ICE/DTLS)나 카메라 캡처가 죽으면 학생 전원이 검은 화면을 보는데,
 *    강사 클라이언트는 연결 상태 이벤트를 하나도 구독하지 않아 감지도 표시도 복구도 없었다.
 *    강사 본인 프리뷰(로컬)와 채팅은 멀쩡하므로 강사는 학생이 말해주기 전까지 모르고,
 *    학생 측 재접속은 송신 측 문제를 고칠 수 없어 60초 주기로 헛돌기만 했다.
 *  - 현장에서 "강사 PC 가 재접속하자 즉시 해소" → 그 재접속을 클라이언트가 스스로 해야 한다.
 *
 * 복구 사다리(가벼운 것부터):
 *  1) lib CONNECTION_INTERRUPTED → 유예(GRACE) 동안 lib 자체 복구(ICE 재시작, enableIceRestart) 대기
 *  2) 미복구 시 라이브러리의 미디어 세션 재시작(_restartMediaSessions = JVB 세션 terminate+Jicofo 재초대).
 *     회의(MUC)를 나가지 않으므로 다른 참가자에게 퇴장/입장으로 보이지 않는다.
 *  3) 그래도 미복구 시 전체 재접속(restartMediaSessions = leave + rejoin, 강사가 손으로 하던 것).
 *     전역 쿨다운·상한을 두어 폭주를 막고, 상한 도달 시 사용자 조치 안내로 종결한다.
 *  카메라 무데이터(no-data)/시그널링 장기 끊김은 가벼운 단계가 무의미하므로 3)으로 바로 간다.
 *
 * 적용 대상: 자가 복구는 송출자(HOST/MANAGER/ADMIN, individualLive)만. 학생의 수신 문제는
 * Video.tsx 의 freezeChecker 가 담당한다(이중 재접속 방지).
 */

const GRACE_INTERRUPTED_MS = 15000; // lib ICE 재시작이 성공하면 보통 이 안에 RESTORED 가 온다
// 시그널링 끊김은 lib(Strophe)이 스스로 소켓을 재개한다(실측: 정상 망 2초 내, 40초 장애도 해제 6초 뒤 복귀).
// 우리가 타이머로 먼저 전체 재접속을 걸면 lib 의 재개를 끊고 오히려 복구를 늦추므로, 재접속은
// lib 가 포기(CONNECTION_FAILED)했을 때 즉시 하고, 그 외에는 아주 긴 안전망만 둔다.
const GRACE_SIGNALING_MS = 120000;
const GRACE_NO_DATA_MS = 10000; // 카메라 무데이터 확인 유예(일시 mute 이벤트 오탐 방지)
const LIGHT_RESTART_WAIT_MS = 15000; // 세션 재시작 후 RESTORED/ESTABLISHED 대기
const FULL_RESTART_COOLDOWN_MS = 60000; // 전체 재접속 최소 간격
const MAX_FULL_RESTARTS = 3; // 접속 세션당 전체 재접속 상한 → 초과 시 failed 안내
const POLL_MS = 5000; // 로컬 카메라 무데이터/검은 프레임 폴링 간격
const BLACK_LUMA_MAX = 12; // 이 이하 평균 밝기(0~255)면 검은 프레임으로 본다
const BLACK_GRACE_MS = 20000; // 검은 프레임이 이 시간 이상 이어지면 안내(조명 순간 변화 오탐 방지)
const OK_RESET_MS = 120000; // 이 시간 이상 정상이면 상한 카운터 초기화

const log = (...args) => console.warn('[senderHealth]', ...args);

const HEARTBEAT_MS = 60000; // 송출자 상태 하트비트 간격
const SEND_STALL_POLLS = 2; // packetsSent 무증가 연속 폴 수(=10초) → 송출 정지 판정
const PEER_CHURN_WINDOW_MS = 60000; // 학생 이탈 폭주 판정 창
const PEER_CHURN_MIN = 3; // 창 안 USER_LEFT 최소 수

/**
 * 서버 진단 로그(fetchErrorData). 모든 mediaHealth 로그에 공통 컨텍스트를 붙인다:
 * 내 jitsi 참가자 id·역할·회의 코드·페이지 가동 시간 — 여러 클라이언트 로그를 한 사고 타임라인으로 엮기 위함.
 * (2026-09 사고: 학생들의 freezeRecovery 로그만 있고 강사 측 로그가 전혀 없어 강사 클라이언트가 무엇을 봤는지 알 수 없었다)
 */
const _pageStart = Date.now();
const _context = () => {
	try {
		const state = APP.store.getState();
		const conf = APP.management?.jitsi?.Conference?.conference;
		return {
			participantId: conf?.myUserId?.() || null,
			role: getLocalMember(state)?.role || null,
			code: state['features/room']?.option?.code || null,
			uptimeSec: Math.round((Date.now() - _pageStart) / 1000),
			visibility: typeof document !== 'undefined' ? document.visibilityState : undefined,
		};
	} catch (e) {
		return {};
	}
};
const diag = async (message, data, options) => {
	try {
		const payload = { ...(data || {}), ...(_context()) };
		console.info('[senderHealth][diag]', message, payload);
		const { fetchErrorData } = await import('../../API/API');
		fetchErrorData({ message: `mediaHealth ${message}`, data: payload }, options);
	} catch (e) { /* 진단 로그 실패는 무시 */ }
};

class SenderHealth {
	constructor() {
		this.conference = null;
		this.state = MEDIA_HEALTH.OK;
		this.since = 0;
		this.graceTimer = null;
		this.lightWaitTimer = null;
		this.pollTimer = null;
		this.signalingDebounce = null;
		this.lightTried = false;
		this.fullCount = 0;
		this.lastFullAt = 0;
		this.okSince = Date.now();
		this._handlers = [];
		this._detached = true;
		this.heartbeatTimer = null;
		this._pc = null; // 상태 전이 리스너를 붙인 송출 RTCPeerConnection
		this._pcHandlers = null;
		this._lastOutbound = null; // 직전 폴의 outbound-rtp(video) 누적치
		this._stallPolls = 0;
		this._lastSnapshot = null; // 언로드 시 함께 보낼 최근 스냅샷
		this._leftAt = []; // USER_LEFT 시각(peer-churn)
		this._churnLoggedAt = 0;
		this._unloadBound = false;
	}

	/** 송출자(자가 복구 대상)인지 — 학생은 Video.tsx freezeChecker 가 담당 */
	_isSender() {
		try {
			const state = APP.store.getState();
			if (getPropertyValue(state, 'individualLive')) return true;
			const role = getLocalMember(state)?.role;

			return role === ROLE.HOST || role === ROLE.MANAGER || role === ROLE.ADMIN;
		} catch (e) {
			return false;
		}
	}

	_set(state, reason) {
		if (this.state !== state) {
			this.state = state;
			this.since = Date.now();
			if (state === MEDIA_HEALTH.OK) this.okSince = this.since;
		}
		try {
			APP.store.dispatch(setMediaHealth(state, reason));
		} catch (e) { /* store 미준비 — 무시 */ }
	}

	_clearTimers() {
		clearTimeout(this.graceTimer);
		clearTimeout(this.lightWaitTimer);
		clearTimeout(this.signalingDebounce);
		this.graceTimer = null;
		this.lightWaitTimer = null;
		this.signalingDebounce = null;
	}

	/**
	 * 회의 입장 완료 후 호출. 이전 회의의 리스너는 detach 로 정리되어 있어야 한다.
	 * 재접속(복구)으로 다시 attach 되면 recovering → ok 로 전환한다.
	 */
	attach(conference) {
		this.detach();
		if (!conference) return;
		this.conference = conference;
		this._detached = false;

		const on = (event, handler) => {
			if (!event) return; // 이 lib 빌드에 없는 이벤트는 건너뜀
			conference.on(event, handler);
			this._handlers.push([event, handler]);
		};

		on(JitsiConferenceEvents.CONNECTION_INTERRUPTED, () => this.onInterrupted('CONNECTION_INTERRUPTED'));
		on(JitsiConferenceEvents.CONNECTION_RESTORED, () => this.onRestored('CONNECTION_RESTORED'));
		on(JitsiConferenceEvents.CONNECTION_ESTABLISHED, () => this.onRestored('CONNECTION_ESTABLISHED'));
		// 입장 중 핸들러(conference.js)는 입장 성공 후 해제되므로 세션 중 ICE 실패는 여기서 받는다
		on(JitsiConferenceEvents.CONFERENCE_FAILED, err => {
			if (err === JitsiConferenceErrors.ICE_FAILED) this.onIceFailed();
			else log('CONFERENCE_FAILED(세션 중):', err);
		});
		on(JitsiConferenceEvents.CONFERENCE_LEFT, () => {
			// 우리가 호출한 leave(정상 퇴장/재접속)면 무시 — reconnect 경로가 detach 를 먼저 호출한다
			if (!this._detached) log('CONFERENCE_LEFT(예기치 않음)');
		});

		// 학생 이탈 폭주 감지 — 내 송출 경로가 죽으면 학생들이 60초 주기로 재접속(퇴장+입장)하는 것이
		// 강사 클라이언트에 보이는 유일한 간접 신호일 수 있다(2026-09 사고: 강사 측은 아무 이벤트도 없었음).
		on(JitsiConferenceEvents.USER_LEFT, () => this._onPeerLeft());

		// 복구 재접속으로 다시 붙은 경우 → 정상 복귀
		if (this.state === MEDIA_HEALTH.RECOVERING) {
			this._set(MEDIA_HEALTH.OK, 'rejoined');
			diag('recovered', { via: 'full-restart', fullCount: this.fullCount });
		}

		this._startPoll();
		this._startHeartbeat();
		this._bindUnload();
		diag('attached', { sender: this._isSender(), participants: conference.getParticipants?.().length });
	}

	// ── 송출 상태 스냅샷(outbound-rtp video / 후보쌍 / pc 상태) ─────────────────────────
	_senderPc() {
		try {
			return this.conference?.getActiveMediaSession?.()?.peerconnection?.peerconnection || null;
		} catch (e) {
			return null;
		}
	}

	async _snapshot() {
		const pc = this._senderPc();
		const out = {
			pcConnection: pc?.connectionState,
			pcIce: pc?.iceConnectionState,
			participants: this.conference?.getParticipants?.().length ?? null,
			state: this.state,
		};
		try {
			const local = getLocalVideoTrack(getTrackState(APP.store.getState));
			const jt = local?.track;
			out.videoMuted = jt ? !!jt.isMuted?.() : null;
			out.videoType = jt?.videoType;
			const mst = jt?.getTrack?.();
			out.trackReady = mst?.readyState;
		} catch (e) { /* 무시 */ }
		if (!pc) return out;
		try {
			const stats = await pc.getStats();
			let pair = null;
			// 시뮬캐스트는 계층(rid)마다 outbound-rtp 가 따로 온다 — 패킷/바이트/프레임은 전 계층 합산(어느 계층이든
			// 나가고 있으면 정지가 아님), 해상도/fps/제한 사유는 가장 큰 계층 값을 대표로 쓴다.
			let top = null;
			stats.forEach(r => {
				if (r.type === 'outbound-rtp' && r.kind === 'video') {
					const o = out.outbound || (out.outbound = { packetsSent: 0, bytesSent: 0, framesSent: 0, framesEncoded: 0, layers: 0, nack: 0, pli: 0 });
					o.packetsSent += r.packetsSent || 0;
					o.bytesSent += r.bytesSent || 0;
					o.framesSent += r.framesSent || 0;
					o.framesEncoded += r.framesEncoded || 0;
					o.nack += r.nackCount || 0;
					o.pli += r.pliCount || 0;
					o.layers += 1;
					if (!top || (r.frameHeight || 0) > (top.frameHeight || 0)) top = r;
				}
				if (r.type === 'remote-inbound-rtp' && r.kind === 'video') {
					out.remote = { lost: r.packetsLost, rtt: r.roundTripTime, jitter: r.jitter, fractionLost: r.fractionLost };
				}
				if (r.type === 'transport') {
					out.transport = { dtls: r.dtlsState, ice: r.iceState, selected: r.selectedCandidatePairId };
				}
				if (r.type === 'candidate-pair' && (r.selected || r.nominated) && r.state === 'succeeded') pair = r;
			});
			if (top && out.outbound) {
				out.outbound.fps = top.framesPerSecond;
				out.outbound.w = top.frameWidth;
				out.outbound.h = top.frameHeight;
				out.outbound.limit = top.qualityLimitationReason;
			}
			if (pair) {
				const local = stats.get(pair.localCandidateId);
				const remote = stats.get(pair.remoteCandidateId);
				out.pair = {
					state: pair.state,
					localType: local?.candidateType,
					remoteType: remote?.candidateType,
					protocol: local?.protocol,
					rtt: pair.currentRoundTripTime,
					bytesSent: pair.bytesSent,
					requestsSent: pair.requestsSent,
					responsesReceived: pair.responsesReceived,
					lastPacketSentMsAgo: pair.lastPacketSentTimestamp ? Math.round(Date.now() - pair.lastPacketSentTimestamp) : undefined,
					lastPacketReceivedMsAgo: pair.lastPacketReceivedTimestamp ? Math.round(Date.now() - pair.lastPacketReceivedTimestamp) : undefined,
				};
			}
		} catch (e) {
			out.statsError = String(e?.message || e).slice(0, 80);
		}
		this._lastSnapshot = out;
		return out;
	}

	/** 송출자 하트비트(60초) — 사고 전후 강사 송출 상태를 타임라인으로 남긴다 */
	_startHeartbeat() {
		clearInterval(this.heartbeatTimer);
		if (!this._isSender()) return;
		this.heartbeatTimer = setInterval(async () => {
			if (this._detached) return;
			diag('heartbeat', await this._snapshot());
		}, HEARTBEAT_MS);
	}

	/** 송출 RTCPeerConnection 의 연결 상태 전이를 그대로 기록(lib 이벤트보다 원시 신호) */
	_bindPcStateListeners() {
		const pc = this._senderPc();
		if (!pc || pc === this._pc) return;
		this._unbindPcStateListeners();
		const onConn = async () => {
			const st = pc.connectionState;
			log('pc connectionState:', st);
			diag('pc-state', { kind: 'connection', to: st, ...(await this._snapshot()) });
		};
		const onIce = async () => {
			const st = pc.iceConnectionState;
			log('pc iceConnectionState:', st);
			diag('pc-state', { kind: 'ice', to: st, ...(await this._snapshot()) });
		};
		pc.addEventListener('connectionstatechange', onConn);
		pc.addEventListener('iceconnectionstatechange', onIce);
		this._pc = pc;
		this._pcHandlers = { onConn, onIce };
	}

	_unbindPcStateListeners() {
		if (this._pc && this._pcHandlers) {
			try {
				this._pc.removeEventListener('connectionstatechange', this._pcHandlers.onConn);
				this._pc.removeEventListener('iceconnectionstatechange', this._pcHandlers.onIce);
			} catch (e) { /* 무시 */ }
		}
		this._pc = null;
		this._pcHandlers = null;
	}

	/** 학생 이탈 폭주: 창 안 USER_LEFT 가 임계 이상이면 1회 기록(내 송출 상태 스냅샷 포함) */
	_onPeerLeft() {
		if (this._detached) return;
		const now = Date.now();
		this._leftAt.push(now);
		this._leftAt = this._leftAt.filter(t => now - t < PEER_CHURN_WINDOW_MS);
		if (this._leftAt.length >= PEER_CHURN_MIN && now - this._churnLoggedAt > PEER_CHURN_WINDOW_MS) {
			this._churnLoggedAt = now;
			log(`학생 이탈 폭주: ${this._leftAt.length}명/${PEER_CHURN_WINDOW_MS / 1000}s — 내 송출 상태 기록`);
			this._snapshot().then(snap => diag('peer-churn', { leftInWindow: this._leftAt.length, ...snap }));
		}
	}

	/** 사용자 퇴장 사유(server/index.js leave) — 강사가 "왜 나갔는지"를 남긴다 */
	noteLeave(reason, detail) {
		diag('leave', { reason, detail: typeof detail === 'string' ? detail.slice(0, 120) : undefined, ...(this._lastSnapshot || {}) });
	}

	/** 페이지 종료(새로고침/탭 닫기/브라우저 종료) — keepalive 로 마지막 상태를 남긴다 */
	_bindUnload() {
		if (this._unloadBound || typeof window === 'undefined') return;
		this._unloadBound = true;
		const onHide = () => {
			if (this._detached) return;
			diag('unload', { state: this.state, ...(this._lastSnapshot || {}) }, { keepalive: true });
		};
		window.addEventListener('pagehide', onHide);
	}

	detach() {
		this._detached = true;
		this._clearTimers();
		clearInterval(this.pollTimer);
		this.pollTimer = null;
		clearInterval(this.heartbeatTimer);
		this.heartbeatTimer = null;
		this._unbindPcStateListeners();
		this._lastOutbound = null;
		this._stallPolls = 0;
		this._disposeProbe();
		if (this.conference) {
			for (const [event, handler] of this._handlers) {
				try { this.conference.off(event, handler); } catch (e) { /* 무시 */ }
			}
		}
		this._handlers = [];
		this.conference = null;
	}

	/** 정상 퇴장(사용자 leave) — 배지도 내린다 */
	reset() {
		this.detach();
		this.lightTried = false;
		this.fullCount = 0;
		this._set(MEDIA_HEALTH.OK, 'reset');
	}

	// ── 이벤트 ───────────────────────────────────────────────────────────────

	onInterrupted(source) {
		if (this._detached) return;
		if (this.state === MEDIA_HEALTH.RECOVERING) return; // 복구 중 신호는 복구 결과로 판정
		log('미디어 연결 끊김:', source);
		diag('interrupted', { source, sender: this._isSender() });
		if (this.state === MEDIA_HEALTH.INTERRUPTED) return;
		this._set(MEDIA_HEALTH.INTERRUPTED, source);

		if (!this._isSender()) return;
		clearTimeout(this.graceTimer);
		this.graceTimer = setTimeout(() => {
			if (this.state === MEDIA_HEALTH.INTERRUPTED) this._recover('interrupted');
		}, GRACE_INTERRUPTED_MS);
	}

	onRestored(source) {
		if (this._detached) return;
		if (this.state === MEDIA_HEALTH.OK) return;
		const wasRecovering = this.state === MEDIA_HEALTH.RECOVERING;
		log('미디어 연결 복구:', source);
		this._clearTimers();
		this._set(MEDIA_HEALTH.OK, source);
		diag('restored', { source, wasRecovering, lightTried: this.lightTried, fullCount: this.fullCount });
		this.lightTried = false;
	}

	onIceFailed() {
		if (this._detached) return;
		log('ICE 실패 확정(lib 재시작 불가/실패)');
		diag('ice-failed', { sender: this._isSender() });
		this._set(MEDIA_HEALTH.INTERRUPTED, 'ICE_FAILED');
		if (!this._isSender()) return;
		// lib 가 이미 재시작을 포기한 상태 — 가벼운 단계는 건너뛰고 전체 재접속
		clearTimeout(this.graceTimer);
		this.graceTimer = setTimeout(() => this._fullRestart('ice-failed'), 2000);
	}

	/** connection.js 에서 호출: 시그널링 소켓 끊김/실패. lib 가 소켓을 스스로 재개하므로 유예 후 판정 */
	onSignaling(kind) {
		if (this._detached) return;
		if (kind === 'established') {
			if (this.state === MEDIA_HEALTH.SIGNALING) this.onRestored('SIGNALING_RESTORED');
			return;
		}
		if (this.state === MEDIA_HEALTH.RECOVERING) return;
		diag('signaling', { kind });

		// lib 가 재연결을 포기한 경우(CONNECTION_FAILED) — 더 기다릴 것이 없다. 송출자는 즉시 전체 재접속.
		if (kind.startsWith('failed')) {
			this._set(MEDIA_HEALTH.SIGNALING, kind);
			if (!this._isSender()) return;
			clearTimeout(this.graceTimer);
			this.graceTimer = setTimeout(() => {
				if (!this._detached && this.state === MEDIA_HEALTH.SIGNALING) this._fullRestart('signaling-failed');
			}, 2000);
			return;
		}

		// 'disconnected': 짧은 끊김은 배지도 띄우지 않는다(5초 디바운스). 재접속은 lib 에 맡기고
		// 아주 긴 안전망(GRACE_SIGNALING_MS)만 둔다.
		clearTimeout(this.signalingDebounce);
		this.signalingDebounce = setTimeout(() => {
			if (this._detached || this._isSignalingConnected()) return;
			this._set(MEDIA_HEALTH.SIGNALING, kind);
			if (!this._isSender()) return;
			clearTimeout(this.graceTimer);
			this.graceTimer = setTimeout(() => {
				if (this._detached || this.state !== MEDIA_HEALTH.SIGNALING) return;
				if (this._isSignalingConnected()) this.onRestored('SIGNALING_RESTORED');
				else this._fullRestart('signaling-stalled');
			}, GRACE_SIGNALING_MS);
		}, 5000);
	}

	/**
	 * 로컬 카메라 트랙의 평균 밝기(0~255). 오프스크린 <video> 에 트랙을 붙여 16×9 로 축소 샘플링한다.
	 * 프레임이 흐르지만 내용이 검정인 경우(카메라 가림·가상카메라 소스 정지·잠긴 화면 공유)는
	 * 어떤 연결 이벤트도 없이 학생 전원이 검은 화면을 보게 되므로(실측 20초 순검정·무반응) 강사에게만 알린다.
	 * @returns {number|null} 측정 불가 시 null
	 */
	_sampleLocalLuma(mst) {
		try {
			if (!mst || mst.readyState !== 'live') return null;
			if (!this._probeVideo) {
				const v = document.createElement('video');
				v.muted = true;
				v.playsInline = true;
				v.autoplay = true;
				v.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;';
				document.body.appendChild(v);
				this._probeVideo = v;
				this._probeCanvas = document.createElement('canvas');
				this._probeCanvas.width = 16;
				this._probeCanvas.height = 9;
			}
			const v = this._probeVideo;
			if (this._probeTrack !== mst) {
				this._probeTrack = mst;
				v.srcObject = new MediaStream([mst]);
				v.play().catch(() => {});
				return null; // 첫 프레임 대기
			}
			if (!v.videoWidth) return null;
			const ctx = this._probeCanvas.getContext('2d');
			ctx.drawImage(v, 0, 0, 16, 9);
			const d = ctx.getImageData(0, 0, 16, 9).data;
			let sum = 0;
			for (let i = 0; i < d.length; i += 4) sum += (d[i] * 299 + d[i + 1] * 587 + d[i + 2] * 114) / 1000;
			return sum / (d.length / 4);
		} catch (e) {
			return null;
		}
	}

	_disposeProbe() {
		try {
			if (this._probeVideo) {
				this._probeVideo.srcObject = null;
				this._probeVideo.remove();
			}
		} catch (e) { /* 무시 */ }
		this._probeVideo = null;
		this._probeCanvas = null;
		this._probeTrack = null;
	}

	_isSignalingConnected() {
		try {
			return !!APP.management?.jitsi?.Connection?.connection?.xmpp?.connection?.connected;
		} catch (e) {
			return false;
		}
	}

	// ── 카메라 무데이터 폴링 ────────────────────────────────────────────────

	_startPoll() {
		clearInterval(this.pollTimer);
		if (!this._isSender()) return;
		let noDataSince = 0;
		let blackSince = 0;
		let blackLogged = false; // 검은 프레임 로그를 남긴 상태(밝아지면 restored 로그 1회)
		this.pollTimer = setInterval(async () => {
			// 송출 정지 감지: 카메라 unmuted·참가자 존재인데 outbound-rtp(video) packetsSent 가 2폴(10초) 연속 무증가.
			// ICE 가 'disconnected' 에 머물거나 브리지가 받지 못하는 "조용한 사망"의 클라이언트 측 유일 증거다
			// (lib CONNECTION_INTERRUPTED 는 이런 경우 오지 않을 수 있다). 감지 시 로그 + 연결 끊김과 동일한 사다리.
			try {
				if (!this._detached && this.state !== MEDIA_HEALTH.RECOVERING && this.state !== MEDIA_HEALTH.FAILED) {
					this._bindPcStateListeners();
					const snap = await this._snapshot();
					const ob = snap.outbound;
					if (ob && snap.videoMuted === false && (snap.participants || 0) > 0) {
						const prev = this._lastOutbound;
						if (prev && ob.packetsSent === prev.packetsSent && ob.bytesSent === prev.bytesSent) {
							this._stallPolls += 1;
							if (this._stallPolls === SEND_STALL_POLLS) {
								log('송출 정지 감지 — packetsSent 무증가 10초');
								diag('send-stall', snap);
								this.onInterrupted('send-stall');
							}
						} else {
							if (this._stallPolls >= SEND_STALL_POLLS && this.state === MEDIA_HEALTH.INTERRUPTED) this.onRestored('SEND_RESUMED');
							this._stallPolls = 0;
						}
						this._lastOutbound = ob;
					} else {
						this._lastOutbound = ob || null;
						this._stallPolls = 0;
					}
				}
			} catch (e) { /* 통계 실패는 무시 */ }

			// document.hidden 으로 걸러내지 않는다 — 강사는 화면공유 중 다른 창에 있어 탭이 백그라운드인 경우가
			// 흔하고, 그때도 카메라 캡처 이상은 계속 감지되어야 한다(백그라운드 타이머 감속은 5초 주기에 무해).
			if (this._detached) return;
			if (this.state === MEDIA_HEALTH.RECOVERING || this.state === MEDIA_HEALTH.FAILED) return;
			let noData = false;
			try {
				const local = getLocalVideoTrack(getTrackState(APP.store.getState));
				const jt = local?.track;
				if (jt) {
					const mst = jt.getTrack?.();
					// ★장치 이탈/캡처 종료: MediaStreamTrack 이 'ended' 가 되면 lib 은 그 로컬 트랙을 muted 로
					//   표시한다(실측). 그래서 muted 를 먼저 걸러내면 이 케이스를 영영 못 본다 — ended 를 먼저 본다.
					//   (사용자 카메라 OFF 는 스트림 자체를 해제해 getTrack() 이 없으므로 여기에 걸리지 않는다)
					const ended = !!(mst && mst.readyState === 'ended');
					// 라이브인데 프레임이 안 오는 경우(lib 자체 감지). 사용자 mute 는 제외.
					const notReceiving =
						!jt.isMuted?.() &&
						typeof jt.isReceivingData === 'function' &&
						jt.isReceivingData() === false;
					noData = ended || notReceiving;
				}
			} catch (e) { noData = false; }

			// 검은 프레임(내용이 검정) 판정 — 무데이터가 아닐 때만. 자동 복구는 하지 않고 강사에게 안내만 한다.
			if (!noData) {
				let luma = null;
				try {
					const local = getLocalVideoTrack(getTrackState(APP.store.getState));
					const jt = local?.track;
					const mst = jt && !jt.isMuted?.() ? jt.getTrack?.() : null;
					luma = mst ? this._sampleLocalLuma(mst) : null;
					if (!mst) this._disposeProbe();
				} catch (e) { luma = null; }
				const isBlack = typeof luma === 'number' && luma <= BLACK_LUMA_MAX;
				// ★로그 전용(2026-10-13 운영 결정): 평균 밝기만 보므로 "검은 배경 + 글씨"(판서·자료 화면) 같은 정상 화면도
				//   검은 프레임으로 잡혀 강사에게 "카메라 영상이 검게 송출되고 있습니다" 배지가 잘못 뜬다.
				//   → 배지(상태 전환) 없이 서버 로그(black-frames / restored)만 남긴다. 다른 감지·복구·배지는 그대로.
				if (isBlack) {
					if (!blackSince) blackSince = Date.now();
					if (!blackLogged && this.state === MEDIA_HEALTH.OK && Date.now() - blackSince >= BLACK_GRACE_MS) {
						blackLogged = true;
						log(`카메라 검은 프레임 지속(밝기 ${luma.toFixed(1)}) — 로그만 기록`);
						diag('black-frames', { luma });
					}
				} else if (typeof luma === 'number') {
					// 실제로 잰 밝기가 기준을 넘었을 때만 "밝아짐" — 측정 불가(null: 트랙 교체 직후 첫 프레임 대기 등)는 판정 보류
					blackSince = 0;
					if (blackLogged) {
						blackLogged = false;
						diag('restored', { source: 'CAMERA_BRIGHT_AGAIN', luma });
					}
				}
			}

			if (!noData) {
				noDataSince = 0;
				if (this.state === MEDIA_HEALTH.NO_DATA) this.onRestored('CAMERA_DATA_RESUMED');
				// 오래 정상이면 상한 카운터 초기화(다음 사고에 다시 3회 허용)
				if (this.state === MEDIA_HEALTH.OK && this.fullCount > 0 && Date.now() - this.okSince > OK_RESET_MS) {
					this.fullCount = 0;
				}
				return;
			}
			if (!noDataSince) noDataSince = Date.now();
			if (this.state !== MEDIA_HEALTH.NO_DATA && Date.now() - noDataSince >= GRACE_NO_DATA_MS) {
				log('카메라 무데이터 지속 — 복구 시도');
				diag('no-data', {});
				this._set(MEDIA_HEALTH.NO_DATA, 'camera');
				// 카메라 재획득은 전체 재접속(_conferenceWillJoin 이 트랙을 새로 만든다)으로만 가능
				this._fullRestart('no-data');
			}
		}, POLL_MS);
	}

	// ── 복구 사다리 ─────────────────────────────────────────────────────────

	_recover(reason) {
		if (this._detached) return;
		if (!this.lightTried) {
			this.lightTried = true;
			this._lightRestart(reason);
			return;
		}
		this._fullRestart(reason);
	}

	/** 2단계: 라이브러리 미디어 세션 재시작(회의 유지, JVB 세션만 terminate → Jicofo 재초대) */
	_lightRestart(reason) {
		const conf = this.conference;
		if (!conf || typeof conf._restartMediaSessions !== 'function') {
			this._fullRestart(reason);
			return;
		}
		log('미디어 세션 재시작(light):', reason);
		diag('light-restart', { reason });
		this._set(MEDIA_HEALTH.RECOVERING, `light:${reason}`);
		try {
			conf._restartMediaSessions();
		} catch (e) {
			log('light restart 실패:', e);
			this._fullRestart(reason);
			return;
		}
		clearTimeout(this.lightWaitTimer);
		this.lightWaitTimer = setTimeout(() => {
			if (this._detached) return;
			if (this.state === MEDIA_HEALTH.RECOVERING) {
				log('light restart 후에도 미복구 → 전체 재접속');
				this._fullRestart(reason);
			}
		}, LIGHT_RESTART_WAIT_MS);
	}

	/** 3단계: 전체 재접속(leave + rejoin). 쿨다운·상한 준수 */
	_fullRestart(reason) {
		if (this._detached) return;
		const now = Date.now();
		if (this.fullCount >= MAX_FULL_RESTARTS) {
			log('전체 재접속 상한 도달 — 사용자 조치 안내');
			diag('failed', { reason, fullCount: this.fullCount });
			this._set(MEDIA_HEALTH.FAILED, reason);
			return;
		}
		const wait = FULL_RESTART_COOLDOWN_MS - (now - this.lastFullAt);
		if (wait > 0) {
			log(`전체 재접속 쿨다운 ${Math.ceil(wait / 1000)}s 대기`);
			this._set(MEDIA_HEALTH.RECOVERING, `cooldown:${reason}`);
			clearTimeout(this.graceTimer);
			this.graceTimer = setTimeout(() => {
				if (!this._detached && this.state !== MEDIA_HEALTH.OK) this._fullRestart(reason);
			}, wait + 500);
			return;
		}
		this.fullCount += 1;
		this.lastFullAt = now;
		this._set(MEDIA_HEALTH.RECOVERING, `full:${reason}`);
		log(`전체 재접속 #${this.fullCount}:`, reason);
		diag('full-restart', { reason, fullCount: this.fullCount });
		// reconnect 경로(jitsi/index.js)가 detach → leave → rejoin → attach 를 수행한다.
		// attach 시 recovering → ok 로 전환되므로 여기서는 결과를 기다리지 않는다.
		Promise.resolve(APP.management.restartMediaSessions()).catch(err => {
			log('전체 재접속 실패:', err);
			diag('full-restart-failed', { reason, error: String(err?.message || err) });
			// 다음 사이클에서 다시 시도(쿨다운 후). 상한은 그대로 누적.
			this.graceTimer = setTimeout(() => {
				if (!this._detached && this.state !== MEDIA_HEALTH.OK) this._fullRestart(reason);
			}, FULL_RESTART_COOLDOWN_MS);
		});
	}
}

const senderHealth = new SenderHealth();

// 진단용(콘솔/자동화 테스트에서 내부 상태 확인). 동작에는 영향 없음.
if (typeof window !== 'undefined') window.__senderHealth = senderHealth;

export default senderHealth;
